import Button from "@components/blocks/Button";
import CardButton from "@components/blocks/CardButton";
import Section from "@components/Section";
import SimpleRichText from "@components/SimpleRichText";
import StrapiImage from "@components/StrapiImage";
import { getSerifClass, isInverted } from "@components/utilities";
import { Icon, Title } from "@justhome/react-ui";
import {
  ComponentSectionsCtaWithFeaturesV2,
  Maybe,
  UploadFileEntityResponse,
} from "@lib/generated/graphql";
import clsx from "clsx";
import { FC } from "react";

interface BookCoverProps {
  title?: Maybe<string>;
  subTitle?: Maybe<string>;
  image?: Maybe<UploadFileEntityResponse>;
  inverted?: boolean;
}

const BookCover: FC<BookCoverProps> = ({
  title,
  inverted,
  subTitle,
  image,
}) => {
  return (
    <div className="max-w-[360px] m:max-w-[560px] relative aspect-[5/7] w-full overflow-hidden">
      <StrapiImage
        fill
        className="object-cover"
        image={image}
        sizes="(max-width: 960px) 80vw,(max-width: 1600px) 30vw, 784px"
      />
      <div className="absolute px-3m pt-[20%]">
        {title && (
          <Title inverted={inverted} className="mb-s text-h2 m:text-d3">
            {title}
          </Title>
        )}
        {subTitle && (
          <p
            className={clsx("text-body-1", {
              "text-neutral-0": inverted,
            })}
          >
            {subTitle}
          </p>
        )}
      </div>
    </div>
  );
};

const CtaWithFeatures: FC<{
  data: ComponentSectionsCtaWithFeaturesV2;
  index: number;
}> = ({ data, index }) => {
  const inverted = isInverted(data.background);
  return (
    <Section
      section={data}
      className="grid gap-3m m:gap grid-cols-1 m:grid-cols-12 bg-primary-50 py-3m m:pb-none m:pt-3xl my-3s"
    >
      <div className="m:col-span-5">
        <Title
          as={index === 0 ? "h1" : "h2"}
          serifClass={getSerifClass(data.background)}
          inverted={inverted}
          className={clsx("text-d3", {
            "text-neutral-0": inverted,
          })}
        >
          {data.title}
        </Title>
        <SimpleRichText
          inverted={inverted}
          className={clsx("my-3s", {
            "text-neutral-0": inverted,
          })}
        >
          {data.body}
        </SimpleRichText>
        <ul className="grid gap grid-cols-2">
          {data.features?.map((feature, index) => (
            <li className="flex" key={index}>
              {feature?.icon && (
                <Icon size="24" className="mr-2xs" type={feature?.icon} />
              )}
              <div
                className={clsx("text-body-2", {
                  "text-neutral-0": inverted,
                })}
              >
                {feature?.text}
              </div>
            </li>
          ))}
        </ul>
      </div>
      <div className="m:col-span-7 m:col-start-7 row-span-2 relative flex justify-center m:justify-end items-end">
        <BookCover
          inverted={inverted}
          title={data.imageTitle}
          subTitle={data.imageSubTitle}
          image={data.image}
        />
      </div>
      <div className="row-start-4 m:row-start-2  m:col-span-5 m:mb-3xl">
        {data.cardButton && <CardButton {...data.cardButton} />}
        {data.button && <Button {...data.button} />}
      </div>
    </Section>
  );
};

export default CtaWithFeatures;
